import { useCallback, useEffect, useState } from 'react'
import { Users } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/contexts/AuthContext'
import { canonicalRole, isSocio } from '@/lib/roles'
import { ApiKeyRecord, apiErrorMessage, teamService, TeamMember } from '@/services/team-service'
import { TeamMembersTable } from '@/components/team/TeamMembersTable'
import { ApiKeysPanel } from '@/components/team/ApiKeysPanel'

/** Configurações › Equipe: papéis, OABs e chaves de API do agente (só sócio altera). */
export function TeamSettingsPage() {
  const { user } = useAuth()
  const canEdit = isSocio(user?.role)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [keys, setKeys] = useState<ApiKeyRecord[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const [loadedMembers, loadedKeys] = await Promise.all([
        teamService.listMembers(),
        canEdit ? teamService.listApiKeys() : Promise.resolve([]),
      ])
      setMembers(loadedMembers)
      setKeys(loadedKeys)
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar a equipe'))
    } finally {
      setLoading(false)
    }
  }, [canEdit])

  useEffect(() => {
    load()
  }, [load])

  const agents = members.filter((m) => canonicalRole(m.role) === 'AGENTE')

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <div className="flex items-center gap-2">
        <Users className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">Equipe</h1>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <Tabs defaultValue="equipe">
        <TabsList>
          <TabsTrigger value="equipe">Papéis e OABs</TabsTrigger>
          {canEdit && <TabsTrigger value="chaves">Chaves de API</TabsTrigger>}
        </TabsList>
        <TabsContent value="equipe">
          <Card>
            <CardHeader>
              <CardTitle>Papéis e OABs</CardTitle>
              <CardDescription>
                OAB com o olho aberto é monitorada na importação de publicações.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Carregando…</p>
              ) : (
                <TeamMembersTable members={members} currentUserId={user?.id} canEdit={canEdit} onChanged={load} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {canEdit && (
          <TabsContent value="chaves">
            <Card>
              <CardHeader>
                <CardTitle>Chaves de API</CardTitle>
                <CardDescription>Acesso do agente (Hermes) à API.</CardDescription>
              </CardHeader>
              <CardContent>
                <ApiKeysPanel keys={keys} agents={agents} onChanged={load} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
